// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import L from 'leaflet';
import myw from 'myWorld-base';
import '../../lib/leaflet/plugins/leaflet.snap';

myw.SnappingPlugin = myw.Plugin.extend('SnappingPlugin', {
    options: {
        snapDistance: 15, // in pixels
        snapVertices: true,
        enabled: true,
        minZoom: 15,
        layers: undefined, //if provided, only (visible) layers with code included in this list will be used for snapping
        excludeLayers: undefined //if provided, layers with code in this list will not be used for snappping, even when visible
    },

    // Actions to be used by map context menu
    actions: {
        toggle: {
            action: 'toggle',
            checked: 'isEnabled'
        }
    },

    /**
     * @class Provides snapping when the user is digitizing geometries
     *        Uses visible layers for snapping guides
     * @param  {Application} owner                       The application
     * @param  {object}  [options]
     * @param  {integer} [options.snapDistance=15]    Maximum distance in pixels for snapping to occur
     * @param  {boolean} [options.snapVertices=true]  If false, it won't snap to intermediate points in a line
     * @param  {boolean} [options.enabled=false]      If true, snapping will be enabled from the start
     * @param  {object}  [options.minZoom=15]         Minimum zoom level for snappping to be active
     * @param  {string[]} [options.layers]            If provided, only (visible) layers with code included in this list will be used for snapping
     * @param  {string[]} [options.excludeLayers]     If provided, layers with code in this list will not be used for snappping, even when visible
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);

        this._engine = new myw.SnapEngine(this.app.map, this.app, this.options);
        if (this.options.enabled) this._engine.enable();
    },

    /**
     * Enables snappping
     */
    enable() {
        this._engine.enable();
    },

    /**
     * Disables snappping
     */
    disable() {
        this._engine.disable();
    },

    /**
     * @return {boolean} True if snapping is current enabled
     */
    isEnabled() {
        return this._engine.isEnabled();
    },

    toggle() {
        if (this.isEnabled()) this.disable();
        else this.enable();
    },

    getState() {
        return {
            enabled: this._engine.isEnabled()
        };
    }
});

myw.SnapEngine = myw.Class.extend('SnapEngine', {
    initialize(map, app, options) {
        this.map = map;
        this.app = app;
        this.options = options;
        this.snap = undefined; //leaflet snap plugin instance
        this._snapMarker = undefined; //marker that will be checked for proximity and sometimes added to map to show it's snapping
        this._snapReady = false;
        this._snapping = false;
        this._lastSnapLatlng = undefined;
        this._enabled = false;
        this._invisibleLayers = {};

        _.bindAll(
            this,
            'checkStatus',
            'snappingStop',
            '_addSnapMarker',
            '_removeSnapMarker',
            'onVertexDragStart',
            'onVertexDragEnd',
            '_onVertexDragSnap',
            'onMarkerDragStart',
            'onMarkerDragEnd',
            'onDrawingStart',
            'onDrawingEnd',
            'onDrawingClick',
            'onDrawingClicked',
            'resetSnappingGuides',
            '_followMouse',
            'onLayerRenderingEnded'
        );
    },

    enable() {
        myw.trace('snapping', 3, 'enable');
        //Start/stop snapping when a feature is edited/finished being edited
        this.map.on('editable:enable zoomend', this.checkStatus);
        this.map.on('editable:disable', this.snappingStop);
        //disable snapping when Ctrl is pressed
        document.addEventListener('keydown', event => {
            if (this.snap && event.which == '17') this.snap.suspend();
        });
        document.addEventListener('keyup', () => this.snap && this.snap.unSuspend());

        this._enabled = true;
        this.checkStatus();
    },

    disable() {
        myw.trace('snapping', 3, 'disable');
        this.map.off('editable:enable zoomend', this.checkStatus);
        this.map.off('editable:disable', this.snappingStop);
        this.snappingStop();
        this._enabled = false;
    },

    isEnabled() {
        return this._enabled;
    },

    checkStatus() {
        const intMode = this.map.currentInteractionMode();
        const inZoom = this.map.getZoom() >= this.options.minZoom;
        const hasSnapLayers = !!this.getSnapLayers().filter(layer => layer.isVisible).length;
        const isUsable = hasSnapLayers && inZoom && intMode instanceof myw.GeomDrawMode;

        myw.trace('snapping', 5, `checkStatus: isUsable: ${isUsable}, snapping: ${this._snapping}`);
        if (isUsable && !this._snapping) this.snappingStart(true);
        else if (!isUsable) this.snappingStop();
    },

    isSnapping() {
        return this._snapping;
    },

    snappingStart(isDrawing = false) {
        myw.trace('snapping', 4, `snapping start. isDrawing: ${isDrawing}`);
        this._snapping = true;
        if (!this._snapReady) {
            this.snap = new L.Handler.MarkerSnap(this.map, this.options);
            //Create snapmarker (requires editable session to be in progress to create the icon - hence doing so now)
            this._snapMarker = L.marker(this.map.getCenter(), {
                icon: this.map.editTools.createVertexIcon({
                    className: this.map.editTools.options.vertexMarkerClass.prototype.options
                        .className
                }),
                opacity: this.map.editTools.options.vertexMarkerClass.prototype.options.opacity,
                zIndexOffset: 1000
            });

            //Pass the snap marker to the snap watchmarker method which fires events when a snap is detected/stops
            this._snapReady = true;
        }
        this.snap.watchMarker(this._snapMarker);
        this.resetSnappingGuides();

        //Show/hide snap marker when a snap starts/stops
        this._snapMarker.on('snap', this._addSnapMarker);
        this._snapMarker.on('unsnap', this._removeSnapMarker);

        this.setMapEventListeners(this.map, 'on');

        //If a user switches a layer off during an edit, we need to reset the snapping guides
        this.app.on('overlayState-changed', this.checkStatus);

        if (isDrawing) this.onDrawingStart.call(this.map);
    },

    snappingStop() {
        myw.trace('snapping', 4, `snapping stop`);
        if (!this._snapping) return;

        this._snapping = false;
        this.onDrawingEnd();
        //Remove event listeners
        this.app.off('overlayState-changed', this.checkStatus);

        this.setMapEventListeners(this.map, 'off');

        this._snapMarker.off('snap', this._addSnapMarker);
        this._snapMarker.off('unsnap', this._removeSnapMarker);
        this.snap.unwatchMarker(this._snapMarker);
        this._removeSnapMarker();
        this.resetSnappingGuides();
        Object.values(this._invisibleLayers).forEach(layer => this.map.removeLayer(layer));
    },

    /*
     * Calculates snap guides from the map's visible vector layers
     */
    resetSnappingGuides() {
        //Clear the snapping guides before adding new ones
        this.snap._guides = [];
        const layers = this.getSnapLayers();
        this.getLeafletLayersFor(layers).forEach(info => {
            const [layer, leafletLayer, onLayerRenderingEnded] = info;
            if (leafletLayer) {
                leafletLayer.off('rendering-ended', onLayerRenderingEnded);
            }
            if (layer.isVisible && this._snapping) {
                const featureReps = leafletLayer.featureRepresentations || {};
                this.updateSnapguides(_.flatten(Object.values(featureReps)));
                leafletLayer.on('rendering-ended', onLayerRenderingEnded);
            }
        });
    },

    /*
     * Obtains a list of layers to be used for snapping guides
     * Only returns myWorld layers.
     * Considers layers and excludeLayers from options
     */
    getSnapLayers() {
        const snappingLayerCodes = [];
        const datasource = this.app.database.getDatasource('myworld'); //Snapping only possible on myworld layers
        datasource.layerDefs.forEach(layerDef => {
            if (layerDef.snap) snappingLayerCodes.push(layerDef.code);
        });
        //Merge from options in js code and db
        const whitelistCodes = this.options.layers
            ? [...new Set(snappingLayerCodes.concat(this.options.layers))] //Merge and remove duplicates
            : snappingLayerCodes;
        const blacklistCodes = this.options.excludeLayers;
        return Object.values(this.map.layerManager.layers).filter(
            layer =>
                layer.datasource.name == 'myworld' &&
                (!whitelistCodes || whitelistCodes.includes(layer.layerDef.code)) &&
                (!blacklistCodes || !blacklistCodes.includes(layer.layerDef.code))
        );
    },

    /*
     * Obtains a list of pairs [layer, leafletLayer] to be used for snapping guides given a list of myWorld layers
     * For tiled layers, 'leafletLayer' will be a paralell, invisible layer so the vector layer code can be used to
     * obtain the geometries.
     * These invisible layers will have been added to the map.
     */
    getLeafletLayersFor(layers) {
        return layers.map(layer => {
            let leafletLayer = layer.leafletLayer;

            if (!leafletLayer.featureRepresentations) {
                //not a vector layer
                //instead return an invisible vector layer that permits obtaining guides
                leafletLayer = this._getInvisibleLayerFor(layer);
            }
            const onLayerRenderingEnded = this.onLayerRenderingEnded.bind(this, leafletLayer);
            return [layer, leafletLayer, onLayerRenderingEnded];
        });
    },

    //returns a new or cached invisible leaflet layer for a given overlay
    _getInvisibleLayerFor(layer) {
        const key = layer.layerDef.code;
        let leafletLayer = this._invisibleLayers[key];
        if (layer.isVisible && !leafletLayer) {
            const datasource = layer.datasource;
            const layerDef = Object.assign({}, layer.layerDef, {
                rendering: 'vector',
                jsClass: myw.MywVectorLayer, // force vector layer class - prevents custom geoserver classes from being obtained in createLayer
                nativeAppVector: {} //necessary to enable snapping of tiled layers in native app
            });
            leafletLayer = this._invisibleLayers[key] = datasource.createLayer(layerDef);
            leafletLayer.setOpacity(0);
        }
        if (layer.isVisible && this._snapping && !leafletLayer._map)
            this.map.addLayer(leafletLayer);
        return this._invisibleLayers[key];
    },

    /*
     * Adds snap guides from a list of myworld feature representations
     * @param {FeatureRepresentations} featureReps
     */
    updateSnapguides(featureReps) {
        featureReps.forEach(featureRep => {
            //If a feature is already in the snapping guide, don't add it again
            const feature = featureRep.feature;
            const coords = feature.geometry.coordinates;
            const guideMatch = this.snap._guides.find(item => item.options.name == feature);

            if (!guideMatch) {
                if (featureRep.feature.geometry.type == 'LineString') {
                    let latLngs = coords.map(c => [c[1], c[0]]);
                    const polyline = L.polyline(latLngs);
                    polyline.options.name = feature;
                    this.snap.addGuideLayer(polyline);
                } else if (featureRep.feature.geometry.type == 'Polygon') {
                    let latLngs = coords[0].map(c => [c[1], c[0]]); //ENH: handle holes
                    const polygon = L.polygon(latLngs);
                    polygon.options.name = feature;
                    this.snap.addGuideLayer(polygon);
                } else if (featureRep.feature.geometry.type == 'Point') {
                    const latLng = [coords[1], coords[0]];
                    const point = L.circle(latLng);
                    point.options.name = feature;
                    this.snap.addGuideLayer(point);
                }
            }
        });
    },

    //called when a layer signals it has finished rendering
    onLayerRenderingEnded(leafletLayer) {
        const featureReps = Object.values(leafletLayer.featureRepresentations);
        this.updateSnapguides(_.flatten(featureReps));
    },

    setMapEventListeners(map, onOrOff) {
        map[onOrOff]('editable:vertex:dragstart', this.onVertexDragStart);
        map[onOrOff]('editable:vertex:dragend', this.onVertexDragEnd);
        map[onOrOff]('editable:dragstart', this.onMarkerDragStart);
        map[onOrOff]('editable:dragend', this.onMarkerDragEnd);
        map[onOrOff]('editable:drawing:start', this.onDrawingStart);
        map[onOrOff]('editable:drawing:end', this.onDrawingEnd);
        map[onOrOff]('editable:drawing:click', this.onDrawingClick);
        map[onOrOff]('editable:drawing:clicked', this.onDrawingClicked);
    },

    //When a vertex of a polyline or polygon is edited
    onVertexDragStart(e) {
        //Pass the polyline's dragged vertex to the watchmarker method
        this.snap.watchMarker(e.vertex);
        e.vertex.on('snap snapping unsnap', this._onVertexDragSnap);
    },

    onVertexDragEnd(e) {
        e.vertex.off('snap', this._onVertexDragSnap);
        this.snap.unwatchMarker(e.vertex);
        if (this._lastSnapLatlng) {
            // e.vertex.latlng = this._lastSnapLatlng;
            e.vertex.latlng.update(this._lastSnapLatlng);
            e.vertex._latlng = e.vertex.latlng; // Push back to Leaflet the editable reference.
        }
    },

    //When a marker is edited
    onMarkerDragStart(e) {
        this._draggingMarker = true;
        this.map.on('editable:drag', this._followMouse); //handles both mouse and touch move events (touchmove is not fired)
    },
    onMarkerDragEnd(e) {
        this._draggingMarker = false;
        //If a snap is active when a marker drag even finishes, set the geometry for the edited feature to the snap geometry
        if (this.map.hasLayer(this._snapMarker)) {
            e.layer.setLatLng(this._snapMarker.getLatLng());
        }
        this._snapMarker.remove();
        this.map.off('editable:drag', this._followMouse);
    },

    //When a new feature is created
    onDrawingStart() {
        this.map.on('mousemove', this._followMouse);
    },
    onDrawingEnd(e) {
        this.map.off('mousemove', this._followMouse);
        this._snapMarker.remove();
    },

    //If we have a snap, push the snapped coordinates to the polygon or polyline feature
    onDrawingClick(e) {
        let latlng = e.latlng;
        if (latlng) this._snapMarker.setLatLng(latlng); //ensures snap marker is up-to-date for touch events

        if (e.layer._latlng) return; // point - handled in drawing clicked

        //adding point to polyline or polygon
        if (this.map.hasLayer(this._snapMarker)) {
            e.layer.editor.push(this._snapMarker.getLatLng());
        }
    },

    onDrawingClicked(e) {
        //If there is a snap when editing a polyline or polygon, remove the vertex that was added via editable as it uses the
        //mouse coordinates and we've already added the snapped coordinates using the 'click' event that fires before editable has done anything
        if (!e.layer._latlng) {
            //line or polygon
            if (this.map.hasLayer(this._snapMarker)) {
                e.layer.editor.pop();
            }
        } else {
            //it is a point and we have a snap, update the coordinates to those of the snapmarker
            if (this.map.hasLayer(this._snapMarker)) {
                e.layer.setLatLng(this._snapMarker.getLatLng());
            }
        }
    },

    _addSnapMarker() {
        const intMode = this.map.currentInteractionMode();
        if (intMode._creating || intMode._appendingVertex || this._draggingMarker) {
            this._snapMarker.addTo(this.map);
        }
    },

    _removeSnapMarker() {
        this._snapMarker.remove();
    },

    //Update the snapMarkers coordinates
    _followMouse(e) {
        this._snapMarker.setLatLng(e.latlng);
    },

    _onVertexDragSnap(e) {
        this._lastSnapLatlng = e.latlng;
    }
});

const SnappingToggleButton = myw.PluginButton.extend({
    id: 'snapping',
    className: 'snapping',
    imgSrc: 'images/actions/snapping.svg',
    titleMsg: 'snapping_button_title',
    initialize(...args) {
        myw.PluginButton.prototype.initialize.call(this, ...args);
        this.owner.app.map.on('editable:enable', () => {
            this.setActive(true);
            this.render();
        });
        this.owner.app.map.on('editable:disable', () => {
            this.setActive(false);
        });
        const intMode = this.app.map.currentInteractionMode();
        this.setActive(intMode instanceof myw.GeomDrawMode);
    },

    action() {
        this.owner.toggle();
        this.render();
    },

    render() {
        if (this.owner.isEnabled()) {
            this.titleMsg = 'button_disable_title';
            this.imgSrc = 'images/toolbar/snapping-active.svg';
        } else {
            this.titleMsg = 'button_enable_title';
            this.imgSrc = 'images/toolbar/snapping.svg';
        }
        this.setTitle(this.titleMsg);
        this.setImage(this.imgSrc);
    }
});

myw.SnappingPlugin.prototype.buttons = {
    toggle: SnappingToggleButton
};

export default myw.SnappingPlugin;
